

const unique = require('array-unique');
const isResolvedResource = require('./is-resolved-resource');
const processError = require('./error-process');

const accumulateLinks = (object, links) => {
  if (Array.isArray(object)) {
    const result = [];

    object.forEach((item, index) => {
      if (isResolvedResource(item)) {
        result.push(accumulateLinks(item, links));
      } else {
        links.push({
          parent: result,
          property: index,
          link: item
        });

        result.push(item);
      }
    });

    return result;
  } else if (object !== null && (typeof object === 'object')) {
    const result = {};

    for (const property in object) {
      if (object.hasOwnProperty(property)) {
        const value = object[property];

        if (isResolvedResource(value)) {
          result[property] = accumulateLinks(value, links);
        } else {
          links.push({
            parent: result,
            property: property,
            link: value
          });
        }
      }
    }

    return result;
  }

  return object;
};

const idsOfType = (links, type) => {
  return unique(
    links.filter((link) => {
      return link.link.sys.linkType === type;
    }).map((link) => {
      return link.link.sys.id;
    })
  );
};

const batches = (original, batchSize) => {
  const data = Array.from(original);
  const batches = [];

  while (data.length) {
    batches.push(data.slice(0, batchSize));
    data.splice(0, batchSize);
  }

  return batches;
};

const resolve = (space, object, resourceMap) => {
  const links = [];
  const result = accumulateLinks(object, links);

  if (!links.length) {
    return Promise.resolve(result);
  }

  // Filter out resources we've already resolved:
  const unresolvedLinks = links.filter((link) => {
    return !resourceMap.has(link.link.sys.id);
  });

  const entryIds = idsOfType(unresolvedLinks, 'Entry');
  const assetIds = idsOfType(unresolvedLinks, 'Asset');
  const contentTypeIds = idsOfType(unresolvedLinks, 'ContentType');

  const promises = [];

  if (entryIds.length) {
    promises.push(
      batches(entryIds, 75).reduce((current, batch) => {
        return current.then(() => {
          return space.getEntries({
            'sys.id[in]': batch.join(','),
            'limit': batch.length
          }).catch(
            processError
          ).then((response) => {
            const entries = response.toPlainObject();

            entries.items.forEach((entry) => {
              resourceMap.set(entry.sys.id, entry);
            });
          });
        });
      }, Promise.resolve())
    );
  }

  if (assetIds.length) {
    promises.push(
      batches(assetIds, 75).reduce((current, batch) => {
        return current.then(() => {
          return space.getAssets({
            'sys.id[in]': batch.join(','),
            'limit': batch.length
          }).catch(
            processError
          ).then((response) => {
            const assets = response.toPlainObject();

            assets.items.forEach((asset) => {
              resourceMap.set(asset.sys.id, asset);
            });
          });
        });
      }, Promise.resolve())
    );
  }

  if (contentTypeIds.length) {
    promises.push(
      batches(contentTypeIds, 75).reduce((current, batch) => {
        return current.then(() => {
          return space.getContentTypes({
            'sys.id[in]': batch.join(','),
            'limit': batch.length
          }).catch(
            processError
          ).then((response) => {
            const contentTypes = response.toPlainObject();

            contentTypes.items.forEach((contentType) => {
              resourceMap.set(contentType.sys.id, contentType);
            });
          });
        });
      }, Promise.resolve())
    );
  }

  return Promise.all(promises).then(() => {
    links.forEach((link) => {
      const resourceId = link.link.sys.id;
      if (!resourceMap.has(resourceId)) {
        // Leave links to resources we haven't resolved alone:
        return;
      }

      link.parent[link.property] = resourceMap.get(resourceId);
    });

    return result;
  });
};

module.exports = (space, object, levels) => {
  let resolving = Promise.resolve(object);
  const resourceMap = new Map();

  levels = parseInt(levels, 10);

  while (levels--) {
    resolving = resolving.then((object) => {
      return resolve(space, object, resourceMap);
    });
  }

  return resolving;
};
